import { Adapt, Bounding, EAdaptMode } from '../modifiers/Adapt';
import { ShapePrimitive } from '../shapes/ShapePrimitive';
/**
 * Create a shape from static buffer
 *
 * @category Shapes.Primitives
 */
class ShapeBuffer extends ShapePrimitive {
    /**
     * Creates an instance of ShapeBuffer.
     *
     * @param {IShapeBufferSettings} [settings={}]
     */
    constructor(settings = {}) {
        settings.type = settings.type || 'ShapeBuffer';
        settings.adaptMode = settings.adaptMode ?? EAdaptMode.Scale;
        super(settings);
        this.adaptMode = settings.adaptMode ?? EAdaptMode.Fill;
        if (typeof settings.shape === 'undefined') {
            console.warn('[Urpflanze:ShapeBuffer] ShapeBuffer require a buffer passed from `shape` property');
            this.shape = ShapeBuffer.EMPTY_BUFFER;
        }
        else {
            this.shape = typeof settings.shape !== 'function' ? Adapt.adapt(settings.shape, this.adaptMode) : settings.shape;
        }
        this.bStatic = this.isStatic();
        this.bStaticIndexed = this.isStaticIndexed();
    }
    /**
     * Check shape is static
     *
     * @returns boolean
     */
    isStatic() {
        return typeof this.shape !== 'function' && super.isStatic();
    }
    /**
     * Check shape is static indexed
     *
     * @returns boolean
     */
    isStaticIndexed() {
        return typeof this.shape !== 'function' && super.isStaticIndexed();
    }
    /**
     *  Unset buffer
     *
     * @param {boolean} [bClearIndexed=false]
     * @param {boolean} [bPropagateToParents=false]
     */
    clearBuffer(bClearIndexed = false, bPropagateToParents = true) {
        super.clearBuffer(bClearIndexed, bPropagateToParents);
        this.shapeBuffer = undefined;
    }
    /**
     * Apply sideLength on <mark>.shape</mark> buffer and calculate bounding
     *
     * @protected
     */
    bindBuffer(propArguments) {
        const sideLength = this.getRepetitionSideLength(propArguments);
        const shapeBuffer = this.applyModifiers((typeof this.shape === 'function' ? this.shape(propArguments) : this.shape).slice(), propArguments);
        const tmpBounding = [undefined, undefined, undefined, undefined];
        for (let i = 0, len = shapeBuffer.length; i < len; i += 2) {
            shapeBuffer[i] = shapeBuffer[i] * sideLength[0];
            shapeBuffer[i + 1] = shapeBuffer[i + 1] * sideLength[1];
            Bounding.add(tmpBounding, shapeBuffer[i], shapeBuffer[i + 1]);
        }
        Bounding.bind(this.currentGenerationPrimitiveBounding, tmpBounding);
        this.shapeBuffer = shapeBuffer;
    }
    /**
     * Return length of buffer
     *
     * @param {IPropArguments} propArguments
     * @returns {number}
     */
    getBufferLength( /*propArguments?: IPropArguments*/) {
        if (this.buffer && this.buffer.length > 0)
            return this.buffer.length;
        return this.shape.length * this.getRepetitionCount();
    }
    /**
     * Return a buffer of children shape or loop generated buffer
     *
     * @protected
     * @param {number} generateId
     * @param {PropArguments} propArguments
     * @returns {Float32Array}
     */
    generateBuffer(generateId, propArguments) {
        if (typeof this.shapeBuffer === 'undefined' ||
            typeof this.props.sideLength === 'function' ||
            typeof this.shape === 'function') {
            this.bindBuffer(propArguments);
        }
        return this.shapeBuffer;
    }
    /**
     * Set shape
     *
     * @param {(Float32Array | (p: IPropArguments) => Float32Array)} [shape]
     */
    setShape(shape) {
        this.shape = typeof shape === 'function' ? shape : Adapt.adapt(shape, this.adaptMode);
        this.clearBuffer(true);
    }
    /**
     * Return adaptMode
     *
     * @returns {EAdaptMode}
     * @memberof ShapeBase
     */
    getAdaptMode() {
        return this.adaptMode;
    }
    /**
     * Get static buffer
     *
     * @param sideLength
     * @returns
     */
    static getBuffer(props = {}) {
        const shape = new this({ ...props, sideLength: props.sideLength || 1 });
        shape.generate();
        return shape.getBuffer() || new Float32Array();
    }
}
export { ShapeBuffer };
//# sourceMappingURL=ShapeBuffer.js.map